import { useQuery } from "@tanstack/vue-query";
import Billing from "@/services/billing/billing";

export interface PromoDiscount {
  code: string | null;
  percentOff: number | null;
  amountOffCents: number | null;
  duration: string | null;
  durationInMonths: number | null;
  endsAt: string | null;
  start?: string | null;
}

export interface BillingSubscriptionStatusData {
  hasSubscription?: boolean;
  status?: string;
  planName?: string;
  currentPeriodEnd?: string;
  currentPeriodStart?: string;
  hasPaymentMethod?: boolean;
  isTrialing?: boolean;
  trialEndsAt?: string;
  monthlyCostCents?: number;
  seatMonthlyCents?: number;
  cancelAtPeriodEnd?: boolean;
  cancelAt?: string;
  discount?: PromoDiscount | null;
  promoCodeApplied?: boolean;
  billingInterval?: "monthly" | "annual";
  pendingDowngrade?: { targetInterval: string; effectiveDate: string } | null;
}

export function useBillingSubscriptionStatus() {
  return useQuery({
    queryKey: ["billing", "subscriptionStatus"] as const,
    queryFn: async (): Promise<BillingSubscriptionStatusData | null> => {
      const result = await Billing.getSubscriptionStatus();
      const payload = result?.data as { status?: boolean; data?: Record<string, unknown> };
      if (payload?.status && payload?.data) {
        const d = payload.data;
        return {
          hasSubscription: d.has_subscription as boolean | undefined,
          status: d.status as string | undefined,
          planName: d.plan as string | undefined,
          currentPeriodEnd: d.current_period_end as string | undefined,
          currentPeriodStart: d.current_period_start as string | undefined,
          hasPaymentMethod: d.has_payment_method as boolean | undefined,
          isTrialing: d.is_trialing as boolean | undefined,
          trialEndsAt: d.trial_ends_at as string | undefined,
          monthlyCostCents: d.monthly_cost_cents as number | undefined,
          seatMonthlyCents: d.seat_monthly_cents as number | undefined,
          cancelAtPeriodEnd: d.cancel_at_period_end as boolean | undefined,
          cancelAt: d.cancel_at as string | undefined,
          discount: d.discount as PromoDiscount | null | undefined,
          promoCodeApplied: d.promo_code_applied as boolean | undefined,
          billingInterval: d.billing_interval as "monthly" | "annual" | undefined,
          pendingDowngrade: d.pending_downgrade
            ? {
                targetInterval: (d.pending_downgrade as any).target_interval,
                effectiveDate: (d.pending_downgrade as any).effective_date,
              }
            : null,
        };
      }
      return null;
    },
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
